import cn from 'classnames'
import { AnimatePresence } from 'framer-motion'
import type { CSSProperties, FC } from 'react'
import React, { cloneElement, isValidElement, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'

import { extendTestData, testProps } from 'utils/test/qaData'

import * as s from './DropDown.module.scss'
import type { DropdownAnimationProps } from './DropDownContent'
import DropDownContent from './DropDownContent'
import { DropDownContext, useDropDownContext } from './context'
import type { IPlacement, IStyleType, ITheme, IType } from './types'
import { useEvents } from './useEvents'
import { useGetPosition } from './useGetPosition'

const TRANSFORM_ORIGIN: Record<IPlacement, React.CSSProperties['transformOrigin']> = {
  bottom: 'bottom center',
  'bottom-start': 'bottom right',
  'bottom-end': 'bottom left',
  left: 'center right',
  'left-end': 'bottom right',
  'left-start': 'top right',
  right: 'center left',
  'right-end': 'bottom left',
  'right-start': 'top left',
  top: 'top center',
  'top-start': 'top right',
  'top-end': 'top left',
}

export interface IDropDownProps extends DropdownAnimationProps {
  children?: ReactNode | FnChild
  overlay?: React.ReactNode | FnChild
  height?: number
  trigger?: ('click' | 'hover' | 'hoverWithoutContent')[]
  textError?: string
  placement?: IPlacement
  fluid?: boolean
  disabled?: boolean
  onVisibleChange?: (visible: boolean) => void
  visible?: boolean
  styleType?: IStyleType
  type?: IType
  offset?: [number, number]
  theme?: ITheme
  overlayStyle?: CSSProperties
  popupContainer?: HTMLElement
  mouseEnterDelay?: number
  name?: string
  closeOnRootClick?: boolean
  stopPropagation?: boolean
  onBlur?: (e: React.FocusEvent<HTMLDivElement> | FocusEvent) => void
  onFocus?: (e: React.FocusEvent<HTMLDivElement> | FocusEvent) => void
  tabIndex?: number
  rootRef?: React.RefObject<HTMLDivElement>
  initialOpen?: boolean
}

export type FnChild = (props: {
  open: boolean
  setOpen: (open: boolean) => void
}) => React.ReactNode

export const DropDown: FC<IDropDownProps> = ({
  children,
  overlay,
  height = 'auto',
  trigger = ['click'],
  textError,
  placement = 'bottom-start',
  fluid,
  disabled,
  onVisibleChange: setControlledOpen,
  visible: controlledOpen,
  type = 'popover',
  styleType = 'defaultDropDown',
  theme,
  offset = [4, 0],
  overlayStyle,
  popupContainer,
  mouseEnterDelay,
  closeOnRootClick = true,
  name,
  stopPropagation,
  onBlur,
  onFocus,
  tabIndex,
  rootRef,
  initialOpen,
  ...rest
}) => {
  const { contextValue, subPopupElements } = useDropDownContext()
  const [focus, setFocus] = useState(false)
  const [popupEle, setPopupEle] = useState<HTMLDivElement | null>(null)
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(Boolean(controlledOpen))
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const localRef = useRef<HTMLDivElement | null>(null)
  const refPortal = useRef<HTMLDivElement | null>(null)
  const refRoot = (rootRef ||
    (children && typeof children === 'object' && 'ref' in children && children?.ref) ||
    localRef) as React.RefObject<HTMLDivElement>
  const rootCn = cn(s.root, { [s.fluid]: fluid, [s.disabled]: disabled })
  const open = useMemo(() => controlledOpen ?? uncontrolledOpen, [controlledOpen, uncontrolledOpen])
  const setOpen = useMemo(
    () => setControlledOpen ?? setUncontrolledOpen,
    [setControlledOpen, setUncontrolledOpen],
  )

  const triggerHoverWithoutContent = trigger.includes('hoverWithoutContent')
  const triggerHover = trigger.includes('hover') || type === 'tooltip'
  const triggerClick = trigger.includes('click') && type !== 'tooltip'
  const defaultTheme = type === 'tooltip' && !theme ? 'dark' : theme || 'light'
  const hoverOpeningDelay = mouseEnterDelay ? mouseEnterDelay * 1000 : 0
  const isTooltipOrHoverTrigger = triggerHover || triggerHoverWithoutContent
  const targetEle = refRoot.current || refPortal.current

  const triggerOpen = useCallback(
    (nextOpen: boolean) => {
      if (open !== nextOpen) {
        setOpen(nextOpen)
      }
    },
    [open, setOpen],
  )

  const positions = useGetPosition({
    targetEle,
    placement,
    popupEle,
    type,
    offset,
    triggerOpen,
    open,
  })

  const styleContent = useMemo(() => ({ ...positions, height }), [height, positions])

  const inPopupOrChild = useCallback(
    (element: Node) => {
      const childDOM = refRoot.current
      if (triggerHoverWithoutContent && type === 'tooltip') {
        return childDOM?.contains(element) || element === childDOM
      }

      return (
        childDOM?.contains(element) ||
        element === childDOM ||
        popupEle?.contains(element) ||
        element === popupEle ||
        (subPopupElements &&
          Object.values(subPopupElements.current).some(
            (subPopupEle) => subPopupEle?.contains(element) || element === subPopupEle,
          ))
      )
    },
    [popupEle, refRoot, subPopupElements, triggerHoverWithoutContent, type],
  )

  const onClickOverlay = useCallback(
    (event: MouseEvent | React.MouseEvent) => {
      if (stopPropagation) {
        event.stopPropagation()
      }

      if (!triggerHoverWithoutContent && !triggerHover && !disabled) {
        triggerOpen(closeOnRootClick ? !open : true)
      }
    },
    [
      stopPropagation,
      triggerHoverWithoutContent,
      triggerHover,
      disabled,
      triggerOpen,
      closeOnRootClick,
      open,
    ],
  )

  const onHoverOverlay = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (!triggerClick && !disabled) {
        triggerOpen(true)
      }
    }, hoverOpeningDelay)
  }, [triggerClick, disabled, triggerOpen, hoverOpeningDelay])

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLDivElement> | FocusEvent) => {
      setFocus(true)
      onFocus?.(e)
    },
    [onFocus],
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement> | FocusEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement
      const portal = popupContainer || refPortal.current
      const blur =
        focus &&
        !refRoot.current?.contains(relatedTarget) &&
        (!portal?.contains(relatedTarget) || !portal)

      if (blur) {
        setFocus(false)
        onBlur?.(e)
        setOpen(false)
      }
    },
    [popupContainer, focus, refRoot, onBlur, setOpen],
  )

  const childClonedEl = useMemo(() => {
    const child = isValidElement(children) ? (
      children
    ) : (
      <div
        {...testProps({ el: 'dropdown', name, open })}
        className={rootCn}
        onClick={onClickOverlay}
        ref={refRoot}
      >
        {typeof children === 'function' ? children({ open, setOpen }) : children}
      </div>
    )

    return cloneElement(child, {
      ...child?.props,
      testData: extendTestData({ el: 'dropdown', name, open, testData: child?.props?.testData }),
      ref: child.props.ref || ('ref' in child && child.ref) || refRoot,
      tabIndex,
    })
  }, [children, rootCn, onClickOverlay, refRoot, open, setOpen, name, tabIndex])

  const handlers = useMemo(
    () => ({ onClickOverlay, onHoverOverlay, handleFocus, handleBlur }),
    [onClickOverlay, onHoverOverlay, handleFocus, handleBlur],
  )

  useEvents({
    inPopupOrChild,
    open,
    popupEle,
    triggerOpen,
    isTooltipOrHoverTrigger,
    timeoutRef,
    refRoot,
    children,
    handlers,
  })

  return (
    <>
      {childClonedEl}
      <DropDownContext.Provider value={contextValue}>
        <AnimatePresence>
          {open && overlay && (
            <DropDownContent
              {...rest}
              open={open}
              overlayStyle={overlayStyle}
              popupContainer={popupContainer}
              refPortal={refPortal}
              setPopupEle={setPopupEle}
              stopPropagation={stopPropagation}
              styleContent={styleContent}
              styleType={styleType}
              textError={textError}
              theme={defaultTheme}
              transformOrigin={TRANSFORM_ORIGIN[placement]}
              type={type}
            >
              {typeof overlay === 'function' ? overlay({ open, setOpen }) : overlay}
            </DropDownContent>
          )}
        </AnimatePresence>
      </DropDownContext.Provider>
    </>
  )
}

export default DropDown
